import { sdk } from '@/lib/sdk'
import type { PublicSubscriptionGroup } from '@/types/checkout'

/**
 * The community's public plans (`GET /api/checkout/subscription-groups`), kept
 * for the visit so `/pricing` and the plan's checkout (`/pricing/{plan}/{option}`)
 * share one answer: the checkout opens on what the list already has, with no
 * page-wide loading, and checks for a newer answer quietly.
 *
 * Keyed by community and session: the list depends on the viewer (their own
 * `link_only` plan, add-ons for plan holders).
 */
interface Entry {
  key: string
  groups: PublicSubscriptionGroup[] | null
  request: Promise<PublicSubscriptionGroup[]> | null
}

let entry: Entry | null = null

const keyFor = (communityId: string) => {
  let token = ''
  try {
    token = localStorage.getItem('memberfy_auth_token') || ''
  } catch {
    /* No storage: one key per community. */
  }
  return `${communityId}:${token.slice(-16)}`
}

const current = (communityId: string): Entry => {
  const key = keyFor(communityId)
  if (!entry || entry.key !== key) entry = { key, groups: null, request: null }
  return entry
}

/** What is already known, without asking the API; null when nothing is. */
export const peekSubscriptionGroups = (communityId: string | undefined): PublicSubscriptionGroup[] | null =>
  communityId ? current(communityId).groups : null

/**
 * The list, from the API. Concurrent callers share one request; `fresh`
 * asks again even when a list is known (the checkout revalidating).
 */
export const loadSubscriptionGroups = (communityId: string, { fresh = false }: { fresh?: boolean } = {}): Promise<PublicSubscriptionGroup[]> => {
  const target = current(communityId)
  if (target.request) return target.request
  if (target.groups && !fresh) return Promise.resolve(target.groups)
  const request = Promise.resolve(sdk.checkout.getSubscriptionGroups())
    .then((response) => {
      const groups = (Array.isArray(response) ? response : []) as PublicSubscriptionGroup[]
      if (entry === target) target.groups = groups
      return groups
    })
    .finally(() => {
      if (entry === target && target.request === request) target.request = null
    })
  target.request = request
  return request
}
